/**
 * Interface representing an observer function.
 */
interface Observer {
  (data?: unknown): void;
}

/**
 * Subscription entry storing observer and its subscribed events.
 */
interface SubscriptionEntry {
  observer: Observer;
  events: Set<string>; // Empty set means subscribe to all events
}

/**
 * Interface representing an observable entity.
 */
interface Observable {
  /**
   * Subscribes an observer to the observable.
   * @param observer - The observer function to be added.
   * @param events - Optional event names to subscribe to. If not provided, subscribes to all events.
   */
  subscribe(observer: Observer, events?: string | string[]): void;

  /**
   * Unsubscribes an observer from the observable.
   * @param observer - The observer function to be removed.
   */
  unsubscribe(observer: Observer): void;

  /**
   * Notifies all subscribed observers.
   * @param events - Optional event name(s) to notify. If not provided, notifies only observers subscribed to all events.
   * @param data - Optional data to pass to the observers.
   */
  notify(events?: string | string[], data?: unknown): void;
}

/**
 * Interface extending Observable for controllers.
 */
export interface IObservableController extends Observable {}

/**
 * Class implementing the ObservableController interface.
 */
export class ObservableController implements IObservableController {
  private subscriptions: Map<Observer, SubscriptionEntry> = new Map();

  /**
   * Subscribes an observer to the observable.
   * @param observer - The observer function to be added.
   * @param events - Optional event name(s) to subscribe to. If not provided, subscribes to all events.
   */
  subscribe(observer: Observer, events?: string | string[]): void {
    const eventSet = new Set<string>();

    if (events) {
      if (typeof events === "string") {
        eventSet.add(events);
      } else {
        for (const event of events) {
          eventSet.add(event);
        }
      }
    }

    this.subscriptions.set(observer, { observer, events: eventSet });
  }

  /**
   * Unsubscribes an observer from the observable.
   * @param observer - The observer function to be removed.
   */
  unsubscribe(observer: Observer): void {
    this.subscriptions.delete(observer);
  }

  /**
   * Notifies all subscribed observers.
   * @param events - Optional event name(s) to notify. If not provided, notifies only observers subscribed to all events.
   */
  notify(events?: string | string[], data?: unknown): void {
    const eventList = typeof events === "string" ? [events] : events ?? [];

    for (const subscription of this.subscriptions.values()) {
      const { observer, events: subscribedEvents } = subscription;

      // If observer subscribed to all events (empty set), notify them
      if (subscribedEvents.size === 0) {
        observer(data);
        continue;
      }

      // Check if any of the notified events match the subscribed events
      for (const event of eventList) {
        if (subscribedEvents.has(event)) {
          observer(data);
          break;
        }
      }
    }
  }

  bindToController(controller: IObservableController): void {
    controller.subscribe(() => this.notify());
  }
}
